import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { type MultiLevelMenuItem } from '@/common/types/multi-level-menu.type';
import { type AllProductsQueryParams } from '@/common/types/product.type';
import { type FilteredDataType } from '@/features/store/types/filter.type';
import {
  selectIsTenant,
  useDomainStore,
} from '@/providers/domain-store-provider';
import { useQueryClient } from '@tanstack/react-query';
import usegetVendorCollectionDetail from '@/common/apis/collection/v1/get-vendor-collection-detail.api';
import { useGetCategoryItem } from '@/common/apis/storefronts/v1/get-category.api';
import { useGetVendorItem } from '@/common/apis/storefronts/v1/get-vendor.api';
import {
  orderByLookup,
  orderMock,
} from '@/common/constants/filter-product-list.constant';
import debounceFunc from '@/common/utils/debounce.utility';

export function useMainLogic() {
  const params = useParams();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const rawCollectionId = Array.isArray(params.id) ? params.id[0] : params.id;
  const rawVendorSlug = Array.isArray(params.slug)
    ? params.slug[0]
    : params.slug;

  const collectionId = Number(rawCollectionId ?? 0);
  const vendorSlug = rawVendorSlug ?? '';

  const isTenant = useDomainStore(selectIsTenant);

  const [categoryListData, setCategoryListData] = useState<
    MultiLevelMenuItem[]
  >([]);

  const [payload, setPayload] = useState<AllProductsQueryParams>({
    searchByProductName: '',
    categoryList: [],
    sortCondition: undefined,
    sortDirection: undefined,
    isAvailable: false,
    isDiscounted: false,
    pageSize: 10,
    pageIndex: 0,
  });

  const [openSearchBox, setOpenSearchBox] = useState(false);

  // Read filters directly from URL search params
  const categories = searchParams.get('category')?.split('-') || [];
  const orderBy = searchParams.get('orderBy') || '';
  const isDiscounted = searchParams.get('isDiscounted') === 'true';
  const isAvailable = searchParams.get('isAvailable') === 'true';

  // Compute filteredData from URL params and categoryListData
  const filteredData: FilteredDataType = {
    category: categories
      .map((cat) => categoryListData.find((c) => c.value === cat))
      .filter(Boolean) as MultiLevelMenuItem[],
    orderBy: orderMock.find((item) => item.value === orderBy) || {
      label: '',
      value: '',
    },
    isDiscounted,
    isAvailable,
  };

  const { data, isLoading } = usegetVendorCollectionDetail({
    assortmentId: collectionId,
  });
  const { data: vendorInfo } = useGetVendorItem({
    query: { identifier: vendorSlug },
    enabled: true,
    disableToast: true,
  });
  const { data: category } = useGetCategoryItem({
    enabled: !!vendorInfo?.data.id,
    query: { vendorId: isTenant ? 0 : vendorInfo?.data.id ?? 0 },
  });

  const payloadSetter = (newPayload: AllProductsQueryParams) => {
    setPayload(newPayload);
  };

  const searchHandler = debounceFunc<(value: string) => void>(
    (value: string) => {
      setPayload((prevPayload) => ({
        ...prevPayload,
        searchByProductName: value,
      }));
    },
    1000
  );

  const searchBarHandler = () => {
    setOpenSearchBox(true);
  };

  const searchBoxCloseHandler = () => {
    setOpenSearchBox(false);
    setPayload({ ...payload, searchByProductName: '' });
  };

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['Products'] });
  }, [payload, queryClient]);

  // Update payload when URL params change
  useEffect(() => {
    // Compute category list directly from categories and categoryListData to avoid filteredData dependency
    const categoryList = categories
      .map((cat) => {
        const found = categoryListData.find((c) => c.value === cat);
        return found ? found.label : '';
      })
      .filter(Boolean);

    setPayload((prevPayload) => ({
      ...prevPayload,
      categoryList,
      sortCondition: orderBy
        ? orderByLookup[orderBy]?.sortCondition
        : undefined,
      sortDirection: orderBy
        ? orderByLookup[orderBy]?.sortDirection
        : undefined,
      isAvailable,
      isDiscounted,
    }));
  }, [
    categories.join(','),
    orderBy,
    isDiscounted,
    isAvailable,
    categoryListData,
  ]);

  useEffect(() => {
    if (category?.data) {
      const refactoredCategory: MultiLevelMenuItem[] =
        category.data.categoryLogItems.map(
          (item: { categoryName: string; categoryId: number }) => ({
            label: item.categoryName,
            value: String(item.categoryId),
            inputType: 'checkbox',
          })
        );
      setCategoryListData(refactoredCategory);
    }
  }, [category]);

  const collection = data || {
    assortmentStatus: '',
    collectionImage: '',
    description: '',
    id: 0,
    isActive: true,
    isMainCollection: true,
    productIds: [],
    title: '',
  };

  return {
    categoryListData,
    filteredData,
    payload,
    payloadSetter,
    openSearchBox,
    searchBarHandler,
    searchBoxCloseHandler,
    searchHandler,
    collection,
    vendorSlug,
    isLoading,
    collectionId,
    vendorInfo,
  };
}
